import { ScheduledOrBlanketEncoded } from '@/modules/document-data-review/models/ScheduledOrBlanket.model';

import { FieldValidator } from '../../models/FieldValidator.model';
import { interpretMessage } from '../../utils/interpret-message/interpretMessage';
import { CustomMessageOptions } from '../type';

const MESSAGE = '{{field}} is required';
const SELF_MESSAGE = 'At least one name is required';

export interface ScheduledOrBlanketItemFieldValidator extends FieldValidator {
    // Validates a sub-field value with access to the full item context
    validateWithContext: (fieldValue: any, itemValue: ScheduledOrBlanketEncoded | null) => string | boolean;
}

/**
 * Creates a $self validator for the entire ScheduledOrBlanket object.
 * Validates that when type is SCHEDULED and names is not null (not "Not Found"),
 * the names array must have at least one item.
 */
export function createScheduledOrBlanketSelfValidator(
    msgOptions?: CustomMessageOptions,
): ScheduledOrBlanketItemFieldValidator {
    const message = msgOptions?.customMsg || SELF_MESSAGE;

    function validateWithContext(
        _fieldValue: any,
        parentValue: ScheduledOrBlanketEncoded | null,
    ): string | boolean {
        // If parent type is not SCHEDULED, no validation needed
        if (parentValue?.type !== 'SCHEDULED') {
            return true;
        }

        // If names is null (Not Found), no validation needed
        if (parentValue?.names === null) {
            return true;
        }

        // Check if names array is empty or undefined
        if (!parentValue?.names || parentValue.names.length === 0) {
            return interpretMessage(message, {
                field: msgOptions?.fieldName || 'Names',
            });
        }

        return true;
    }

    // Standard validate function for compatibility (validates the entire value)
    function validate(value: any): string | boolean {
        const parentValue = value as ScheduledOrBlanketEncoded | null;

        if (parentValue?.type !== 'SCHEDULED') {
            return true;
        }

        if (parentValue?.names === null) {
            return true;
        }

        if (!parentValue?.names || parentValue.names.length === 0) {
            return interpretMessage(message, {
                field: msgOptions?.fieldName || 'Names',
            });
        }

        return true;
    }

    return {
        name: 'list.nonEmpty',
        validate,
        validateWithContext,
    };
}

/**
 * Creates a required validator for name items in the names array.
 * Only validates when parent type is 'SCHEDULED'.
 */
export function createScheduledNameItemRequiredValidator(
    msgOptions?: CustomMessageOptions,
): ScheduledOrBlanketItemFieldValidator {
    const message = msgOptions?.customMsg || MESSAGE;

    function validateWithContext(
        fieldValue: any,
        parentValue: ScheduledOrBlanketEncoded | null,
    ): string | boolean {
        // If parent type is not SCHEDULED, no validation needed
        if (parentValue?.type !== 'SCHEDULED') {
            return true;
        }

        // If names is null (Not Found), no validation needed
        if (parentValue?.names === null) {
            return true;
        }

        // Check if the name value is empty
        if (fieldValue === undefined || fieldValue === null || fieldValue === '' ||
            (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
            return interpretMessage(message, {
                field: msgOptions?.fieldName || 'Name',
            });
        }

        return true;
    }

    // Standard validate function for compatibility (validates without context)
    function validate(fieldValue: any): string | boolean {
        if (fieldValue === undefined || fieldValue === null || fieldValue === '' ||
            (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
            return interpretMessage(message, {
                field: msgOptions?.fieldName || 'Name',
            });
        }
        return true;
    }

    return {
        name: 'required',
        validate,
        validateWithContext,
    };
}
